import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  LogOut,
  Users,
  Building2,
  Briefcase,
  TrendingUp,
  Plus,
  Trash2,
  Eye,
  RefreshCw,
  Mail,
  Phone,
  MapPin,
  Trophy,
  Star,
  Clock,
  AlertCircle,
  Download,
  CheckCircle,
  XCircle,
  UserCheck,
  UserX,
  Calendar,
  DollarSign,
  Target,
  Award,
  Shield,
  ChevronRight,
  MessageSquare,
  FileText,
  Pencil,
  ExternalLink,
  Settings,
  Sparkles, // Ajoutez ceci
  Check, // Ajoutez ceci
  SkipForward, // Ajoutez ceci
  ChevronDown, // Ajouter pour l'accordéon
  ChevronUp, // Ajouter pour l'accordéon
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { crmImport } from "@/services/crmImport.service";

const STATUT_CEDANT = [
  { value: "nouveau", label: "Nouveau", color: "bg-blue-100 text-blue-800" },
  {
    value: "contacte",
    label: "Contacté",
    color: "bg-yellow-100 text-yellow-800",
  },
  {
    value: "qualifie",
    label: "Qualifié",
    color: "bg-green-100 text-green-800",
  },
  {
    value: "accompagne",
    label: "Accompagné",
    color: "bg-purple-100 text-purple-800",
  },
];

const STATUT_INTERET = [
  { value: "nouveau", label: "Nouveau" },
  { value: "en_cours", label: "En cours" },
  { value: "termine", label: "Terminé" },
  { value: "annule", label: "Annulé" },
];

const STATUT_REPRENEUR = [
  { value: "nouveau", label: "Nouveau", color: "bg-blue-100 text-blue-800" },
  {
    value: "contacte",
    label: "Contacté",
    color: "bg-yellow-100 text-yellow-800",
  },
  { value: "actif", label: "Actif", color: "bg-green-100 text-green-800" },
  {
    value: "en_discussion",
    label: "En discussion",
    color: "bg-purple-100 text-purple-800",
  },
];

const NIVEAU_CONFIG = {
  premium: { icon: Trophy, color: "text-accent", bg: "bg-accent/10" },
  qualifie: { icon: Star, color: "text-primary", bg: "bg-primary/10" },
  en_construction: {
    icon: Clock,
    color: "text-orange-600",
    bg: "bg-orange-100",
  },
  curieux: { icon: AlertCircle, color: "text-foreground/40", bg: "bg-muted" },
};

const SECTEURS = [
  { value: "industrie", label: "Industrie" },
  { value: "services", label: "Services" },
  { value: "commerce", label: "Commerce" },
  { value: "artisanat", label: "Artisanat" },
  { value: "tech", label: "Tech" },
  { value: "sante", label: "Santé" },
  { value: "batiment", label: "BTP" },
  { value: "restauration", label: "Restauration" },
];

const LOCALISATIONS = [
  { value: "aura", label: "Auvergne-Rhône-Alpes" },
  { value: "paca", label: "PACA" },
  { value: "occitanie", label: "Occitanie" },
];

const CAPACITE_OPTIONS = [
  { value: "moins_100k", label: "Moins de 100 000 €" },
  { value: "100k_200k", label: "100 000 € - 200 000 €" },
  { value: "200k_500k", label: "200 000 € - 500 000 €" },
  { value: "500k_1m", label: "500 000 € - 1 M€" },
  { value: "plus_1m", label: "Plus de 1 M€" },
];

const EXPERIENCE_OPTIONS = [
  { value: "salarie", label: "Salarié sans expérience de management" },
  { value: "manager", label: "Manager / Chef d'équipe" },
  { value: "cadre_dirigeant", label: "Cadre dirigeant" },
  { value: "dirigeant_actuel", label: "Dirigeant actuel" },
  { value: "ex_dirigeant", label: "Ex-dirigeant / Entrepreneur" },
];

const HORIZON_OPTIONS = [
  { value: "moins_3mois", label: "Dans les 3 prochains mois" },
  { value: "3_6mois", label: "Dans 3 à 6 mois" },
  { value: "6_12mois", label: "Dans 6 à 12 mois" },
  { value: "plus_12mois", label: "Dans plus de 12 mois (veille)" },
];

const ACCORD_OPTIONS = [
  { value: "oui", label: "Oui, j'ai un pré-accord" },
  { value: "en_cours", label: "Discussions en cours" },
  { value: "non", label: "Pas encore contacté" },
];

const BUDGET_OPTIONS = [
  { value: "moins_200k", label: "Moins de 200 000 €" },
  { value: "200k_500k", label: "200 000 € - 500 000 €" },
  { value: "500k_1m", label: "500 000 € - 1 M€" },
  { value: "1m_3m", label: "1 M€ - 3 M€" },
  { value: "plus_3m", label: "Plus de 3 M€" },
];

const DISPONIBILITE_OPTIONS = [
  { value: "temps_plein", label: "Temps plein immédiat" },
  { value: "transition", label: "Période de transition nécessaire" },
  { value: "temps_partiel", label: "Temps partiel au début" },
];

const ACCOMPAGNEMENT_OPTIONS = [
  { value: "autonome", label: "Autonome - Peu d'accompagnement" },
  { value: "ponctuel", label: "Accompagnement ponctuel" },
  { value: "complet", label: "Accompagnement complet souhaité" },
];
// Version JavaScript (sans types)
// Ajoutez ces constantes pour la documentation
const RecommendationShape = {
  recommendation_id: "",
  repreneur_id: 0,
  repreneur_nom: "",
  opportunite_id: 0,
  opportunite_titre: "",
  score: 0,
  score_details: {
    budget: 0,
    location: 0,
    sector: 0,
    total: 0,
  },
  status: "pending",
  created_at: "",
  expires_at: "",
  match_reason: "",
};

const DecisionResponseShape = {
  recommendation_id: "",
  action: "accept", // ou 'skip'
  repreneur_id: 0,
  opportunite_id: 0,
  timestamp: "",
};
export default function AdminDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [cedants, setCedants] = useState([]);
  const [allCedantsRaw, setAllCedantsRaw] = useState([]);
  const [repreneurs, setRepreneurs] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [opportunites, setOpportunites] = useState([]);
  const [interets, setInterets] = useState([]);
  const [systemSettings, setSystemSettings] = useState({ nda_required: true });
  const [selectedInteret, setSelectedInteret] = useState(null);
  const [editInteretData, setEditInteretData] = useState(null);
  const [activeTab, setActiveTab] = useState("cedants");

  const [pendingCedants, setPendingCedants] = useState([]);
  const [pendingRepreneurs, setPendingRepreneurs] = useState([]);
  const [rejectedCedants, setRejectedCedants] = useState([]);
  const [rejectedRepreneurs, setRejectedRepreneurs] = useState([]);

  // Modal states
  const [showNewOpportunite, setShowNewOpportunite] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newOpportunite, setNewOpportunite] = useState({
    titre: "",
    secteur: "",
    description: "",
    localisation: "",
    departement: "",
    chiffre_affaires: "",
    effectif: "",
    valorisation_min: 0,
    valorisation_max: 0,
    points_forts: "",
    nom_entreprise: "",
    memorandum: "",
    chiffre_affaires_n1: "",
    chiffre_affaires_moyen: "",
    contact: "",
    notes: "",
    cedant_id: "",
  });
  const [selectedCedant, setSelectedCedant] = useState(null);
  const [editCedantData, setEditCedantData] = useState(null);
  const [selectedRepreneur, setSelectedRepreneur] = useState(null);
  const [editRepreneurData, setEditRepreneurData] = useState(null);

  const [showChangePassword, setShowChangePassword] = useState(false);
  const [changePasswordData, setChangePasswordData] = useState({
    ancien_mot_de_passe: "",
    nouveau_mot_de_passe: "",
    confirm_mot_de_passe: "",
  });

  const [recommendations, setRecommendations] = useState([]);
  const [selectedRecommendation, setSelectedRecommendation] = useState(null);
  const [processingRecommendation, setProcessingRecommendation] =
    useState(null);

  // Ajouter avec les autres useState
  const [groupedRecommendations, setGroupedRecommendations] = useState([]);
  const [expandedRepreneurs, setExpandedRepreneurs] = useState({});
  const [loadingMatches, setLoadingMatches] = useState(false);
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin");
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);
  // Récupérer les recommandations via le backend FastAPI
  // Remplacer la fonction loadRecommendations existante par celle-ci
  const loadRecommendations = useCallback(async () => {
    const headers = getAuthHeaders();
    if (!headers) return;

    setLoadingMatches(true);
    try {
      // Nouvel endpoint groupé par repreneur
      const response = await axios.get(`${API}/recommendations/best`, {
        headers,
      });

      const data = response.data;
      console.log("📊 Recommandations groupées:", data);

      setGroupedRecommendations(data.repreneurs || []);

      // Garder aussi le format plat pour compatibilité
      const allRecommendations = [];
      for (const rep of data.repreneurs || []) {
        for (const opp of rep.opportunities || []) {
          allRecommendations.push({
            recommendation_id: opp.recommendation_id,
            repreneur_id: rep.repreneur_id,
            repreneur_nom: rep.repreneur_nom,
            opportunite_id: opp.opportunite_id,
            opportunite_titre: opp.opportunite_titre,
            score: opp.score,
            score_details: opp.score_details,
            status: "pending",
            match_reason: opp.match_reason,
          });
        }
      }
      setRecommendations(allRecommendations);
    } catch (error) {
      console.error("Erreur chargement recommandations:", error);
      if (error.response?.status !== 404) {
        toast.error("Erreur chargement des recommandations");
      }
    } finally {
      setLoadingMatches(false);
    }
  }, [getAuthHeaders]);
  // Ajoutez ce useEffect après les autres useEffects (vers ligne 450)
  useEffect(() => {
    if (activeTab === "recommendations") {
      loadRecommendations();
    }
  }, [activeTab, loadRecommendations]);
  // Mettre à jour la fonction existante
  const handleAcceptRecommendation = async (recommendation) => {
    setProcessingRecommendation(recommendation.recommendation_id);

    const headers = getAuthHeaders();
    if (!headers) {
      setProcessingRecommendation(null);
      return;
    }

    const decision = {
      recommendation_id: recommendation.recommendation_id,
      action: "accept",
      repreneur_id: recommendation.repreneur_id,
      opportunite_id: recommendation.opportunite_id,
      timestamp: new Date().toISOString(),
    };

    try {
      const response = await axios.post(
        `${API}/recommendations/decision`,
        decision,
        {
          headers,
        },
      );
      toast.success(`Match accepté avec ${recommendation.repreneur_nom}`);
      await loadRecommendations();
      setSelectedRecommendation(null);
    } catch (error) {
      console.error("Erreur:", error);
      toast.error(
        error.response?.data?.detail || "Erreur lors de l'acceptation",
      );
    } finally {
      setProcessingRecommendation(null);
    }
  };

  // Ignorer une recommandation via le backend
  // Mettre à jour la fonction existante
  const handleSkipRecommendation = async (recommendation) => {
    setProcessingRecommendation(recommendation.recommendation_id);

    const headers = getAuthHeaders();
    if (!headers) {
      setProcessingRecommendation(null);
      return;
    }

    const decision = {
      recommendation_id: recommendation.recommendation_id,
      action: "skip",
      repreneur_id: recommendation.repreneur_id,
      opportunite_id: recommendation.opportunite_id,
      timestamp: new Date().toISOString(),
    };

    try {
      await axios.post(`${API}/recommendations/decision`, decision, {
        headers,
      });

      toast.info(`Match ignoré: ${recommendation.repreneur_nom}`);
      await loadRecommendations();
      setSelectedRecommendation(null);
    } catch (error) {
      console.error("Erreur:", error);
      toast.error(error.response?.data?.detail || "Erreur lors du traitement");
    } finally {
      setProcessingRecommendation(null);
    }
  };
  // Ajouter cette fonction
  const toggleRepreneurExpand = (repreneurId) => {
    setExpandedRepreneurs((prev) => ({
      ...prev,
      [repreneurId]: !prev[repreneurId],
    }));
  };
  useEffect(() => {
    if (selectedCedant) {
      setEditCedantData({
        ...selectedCedant,
        valuation: { ...selectedCedant.valuation },
      });
    } else {
      setEditCedantData(null);
    }
  }, [selectedCedant]);

  useEffect(() => {
    if (selectedRepreneur) {
      setEditRepreneurData({
        ...selectedRepreneur,
        qualification: { ...selectedRepreneur.qualification },
      });
    } else {
      setEditRepreneurData(null);
    }
  }, [selectedRepreneur]);

  const loadData = useCallback(async () => {
    setLoading(true);
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      const [
        statsRes,
        cedantsRes,
        repreneursRes,
        allUsersRes,
        opportunitesRes,
        interetsRes,
        pendingRes,
        rejectedRes,
        settingsRes,
      ] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/cedants`, { headers }),
        axios.get(`${API}/repreneurs`, { headers }),
        axios.get(`${API}/admin/users/all`, { headers }),
        axios.get(`${API}/opportunites`),
        axios.get(`${API}/interets`, { headers }),
        axios.get(`${API}/validation/pending`, { headers }),
        axios.get(`${API}/validation/rejected`, { headers }),
        axios.get(`${API}/settings/admin`, { headers }),
      ]);

      setStats(statsRes.data);
      setSystemSettings(settingsRes.data);
      setAllCedantsRaw(cedantsRes.data);
      setCedants(cedantsRes.data.filter((c) => c.validation === true));
      setRepreneurs(repreneursRes.data.filter((r) => r.validation === true));
      setAllUsers(allUsersRes.data);
      setOpportunites(opportunitesRes.data);
      setInterets(interetsRes.data);

      const pendingUsers = pendingRes.data.pending_users || [];
      setPendingCedants(
        pendingUsers.filter(
          (u) =>
            u.user_type === "cedant" && !u.rejected_at && u.validation !== true,
        ),
      );
      setPendingRepreneurs(
        pendingUsers.filter(
          (u) =>
            u.user_type === "repreneur" &&
            !u.rejected_at &&
            u.validation !== true,
        ),
      );

      const rejectedUsers = rejectedRes.data.rejected_users || [];
      setRejectedCedants(rejectedUsers.filter((u) => u.user_type === "cedant"));
      setRejectedRepreneurs(
        rejectedUsers.filter((u) => u.user_type === "repreneur"),
      );
    } catch (error) {
      console.error("Load data error:", error);
      if (error.response?.status === 401) {
        toast.error("Session expirée. Reconnexion requise.");
        localStorage.removeItem("admin_token");
        localStorage.removeItem("admin_info");
        navigate("/admin");
      } else {
        toast.error("Erreur chargement données");
      }
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, navigate]);

  useEffect(() => {
    if (!localStorage.getItem("admin_token")) {
      navigate("/admin");
      return;
    }
    loadData();
  }, [navigate, loadData]);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_info");
    navigate("/admin");
  };

  const handleExport = async () => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      const response = await axios.get(
        `${API}/export/${activeTab}?format=xlsx`,
        {
          headers,
          responseType: "blob",
        },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;

      // Get filename from header if possible
      const contentDisposition = response.headers["content-disposition"];
      let filename = `${activeTab}_export.xlsx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) filename = filenameMatch[1];
      }

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export réussi");
    } catch (error) {
      toast.error("Erreur lors de l'export");
    }
  };

  const handleUpdateStatus = async (type, id, newStatus) => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      await axios.patch(
        `${API}/${type}/${id}/status?statut=${newStatus}`,
        {},
        { headers },
      );
      toast.success("Statut mis à jour");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleDeleteItem = async (type, id) => {
    const headers = getAuthHeaders();
    if (!headers) return;

    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet élément ?"))
      return;

    try {
      await axios.delete(`${API}/admin/${type}/${id}`, { headers });
      toast.success("Élément supprimé");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // new validation
  const handleValidateUser = async (userType, userId) => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      // Map user_type to match backend expectations
      const user_type =
        userType === "cedants" || userType === "cedant"
          ? "cedant"
          : "repreneur";

      // Get user data for CRM import before validation
      const userData = allUsers.find((u) => u.id === userId);

      await axios.post(
        `${API}/validation/validate-user`,
        { user_id: userId, user_type: user_type },
        { headers },
      );

      toast.success(
        `${userType === "cedants" ? "Cédant" : "Repreneur"} validé avec succès`,
      );

      // CRM Import - Async fire and notify
      if (userData) {
        const importMethod =
          user_type === "cedant"
            ? crmImport.importCedant(userData)
            : crmImport.importRepreneur(userData);

        importMethod
          .then((result) => {
            if (result.success) {
              toast.success("Importé dans le CRM");
            } else {
              toast.warning(
                `Utilisateur validé mais l'import CRM a échoué: ${result.details?.[0]?.error || "Erreur inconnue"}`,
              );
            }
          })
          .catch((err) => {
            toast.error(
              `Utilisateur validé mais l'import CRM a échoué: ${err.message}`,
            );
          });
      }

      loadData(); // Recharge toutes les données
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("Utilisateur non trouvé");
      } else if (error.response?.status === 401) {
        toast.error("Session expirée. Veuillez vous reconnecter.");
        localStorage.removeItem("admin_auth");
        navigate("/admin");
      } else {
        toast.error("Erreur lors de la validation");
      }
    }
  };

  // New reject function
  const handleRejectUser = async (userType, userId) => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      const user_type =
        userType === "cedants" || userType === "cedant"
          ? "cedant"
          : "repreneur";

      await axios.post(
        `${API}/validation/reject-user`,
        { user_id: userId, user_type: user_type },
        { headers },
      );

      toast.success(
        `${userType === "cedants" ? "Cédant" : "Repreneur"} rejeté`,
      );
      loadData();
    } catch (error) {
      toast.error("Erreur lors du rejet");
    }
  };

  const handleUpdateRepreneur = async () => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      const payload = {
        email: editRepreneurData.email,
        prenom: editRepreneurData.prenom,
        nom: editRepreneurData.nom,
        telephone: editRepreneurData.telephone,
        qualification: {
          capacite_financiere:
            editRepreneurData.qualification.capacite_financiere,
          experience: editRepreneurData.qualification.experience,
          horizon_projet: editRepreneurData.qualification.horizon_projet,
          secteurs_cibles: Array.isArray(
            editRepreneurData.qualification.secteurs_cibles,
          )
            ? editRepreneurData.qualification.secteurs_cibles
            : (editRepreneurData.qualification.secteurs_cibles || "")
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean),
          zones_geographiques: Array.isArray(
            editRepreneurData.qualification.zones_geographiques,
          )
            ? editRepreneurData.qualification.zones_geographiques
            : (editRepreneurData.qualification.zones_geographiques || "")
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean),
          pre_accord_bancaire:
            editRepreneurData.qualification.pre_accord_bancaire,
          motivation: editRepreneurData.qualification.motivation,
          disponibilite: editRepreneurData.qualification.disponibilite,
          accompagnement_souhaite:
            editRepreneurData.qualification.accompagnement_souhaite,
          budget_max: editRepreneurData.qualification.budget_max,
        },
        memorandum: editRepreneurData.memorandum,
        secteur_activite: editRepreneurData.secteur_activite,
        chiffre_affaires_n1: editRepreneurData.chiffre_affaires_n1,
        chiffre_affaires_moyen: editRepreneurData.chiffre_affaires_moyen,
        contact: editRepreneurData.contact,
        notes: editRepreneurData.notes,
        statut: editRepreneurData.statut,
        validation: editRepreneurData.validation,
      };

      await axios.put(`${API}/repreneurs/${editRepreneurData.id}`, payload, {
        headers,
      });
      toast.success("Repreneur mis à jour avec succès");
      setSelectedRepreneur(null);
      loadData();
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la mise à jour du repreneur");
    }
  };

  const handleUpdateCedant = async () => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      const payload = {
        email: editCedantData.email,
        prenom: editCedantData.prenom,
        nom: editCedantData.nom,
        telephone: editCedantData.telephone,
        entreprise: editCedantData.entreprise,
        localisation: editCedantData.localisation,
        horizon_cession: editCedantData.horizon_cession,
        motivation: editCedantData.motivation,
        valuation: {
          secteur: editCedantData.valuation.secteur,
          chiffre_affaires: editCedantData.valuation.chiffre_affaires,
          resultat_net: editCedantData.valuation.resultat_net,
          effectif: editCedantData.valuation.effectif,
          anciennete: editCedantData.valuation.anciennete,
        },
        memorandum: editCedantData.memorandum,
        secteur_activite: editCedantData.secteur_activite,
        chiffre_affaires_n1: editCedantData.chiffre_affaires_n1,
        chiffre_affaires_moyen: editCedantData.chiffre_affaires_moyen,
        contact: editCedantData.contact,
        notes: editCedantData.notes,
        statut: editCedantData.statut,
        validation: editCedantData.validation,
      };

      await axios.put(`${API}/cedants/${editCedantData.id}`, payload, {
        headers,
      });
      toast.success("Cédant mis à jour avec succès");
      setSelectedCedant(null);
      loadData();
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la mise à jour du cédant");
    }
  };

  const handleEdit = (opp) => {
    setEditingId(opp.id);
    setNewOpportunite({
      ...opp,
      points_forts: Array.isArray(opp.points_forts)
        ? opp.points_forts.join(", ")
        : opp.points_forts || "",
    });
    setShowNewOpportunite(true);
  };

  const handleChangePassword = async () => {
    if (
      changePasswordData.nouveau_mot_de_passe !==
      changePasswordData.confirm_mot_de_passe
    ) {
      toast.error("Les nouveaux mots de passe ne correspondent pas");
      return;
    }
    if (changePasswordData.nouveau_mot_de_passe.length < 8) {
      toast.error("Le nouveau mot de passe doit faire au moins 8 caractères");
      return;
    }

    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      await axios.post(
        `${API}/admin/change-password`,
        {
          ancien_mot_de_passe: changePasswordData.ancien_mot_de_passe,
          nouveau_mot_de_passe: changePasswordData.nouveau_mot_de_passe,
        },
        { headers },
      );

      toast.success("Mot de passe changé avec succès");
      setShowChangePassword(false);
      setChangePasswordData({
        ancien_mot_de_passe: "",
        nouveau_mot_de_passe: "",
        confirm_mot_de_passe: "",
      });
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
          "Erreur lors du changement de mot de passe",
      );
    }
  };

  const handleUpdateInterest = async () => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      await axios.put(
        `${API}/interets/${editInteretData.id}`,
        {
          statut: editInteretData.statut,
          message: editInteretData.message,
        },
        { headers },
      );
      toast.success("Intérêt mis à jour");
      setEditInteretData(null);
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleCreateOpportunite = async () => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      const payload = {
        ...newOpportunite,
        valorisation_min: parseInt(newOpportunite.valorisation_min) || 0,
        valorisation_max: parseInt(newOpportunite.valorisation_max) || 0,
        chiffre_affaires: newOpportunite.chiffre_affaires.toString(),
        points_forts:
          typeof newOpportunite.points_forts === "string"
            ? newOpportunite.points_forts
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
            : newOpportunite.points_forts || [],
      };

      let createdOpp = null;
      if (editingId) {
        const response = await axios.put(
          `${API}/opportunites/${editingId}`,
          payload,
          {
            headers,
          },
        );
        createdOpp = response.data;
        toast.success("Opportunité mise à jour");
      } else {
        const response = await axios.post(`${API}/opportunites`, payload, {
          headers,
        });
        createdOpp = response.data;
        toast.success("Opportunité créée");
      }

      // CRM Import for Opportunities - Async fire and notify
      if (createdOpp) {
        crmImport
          .importOpportunity(createdOpp)
          .then((result) => {
            if (result.success) {
              toast.success("Opportunité exportée vers le CRM");
            } else {
              toast.warning(
                `Opportunité créée mais l'export CRM a échoué: ${result.details?.[0]?.error || "Erreur inconnue"}`,
              );
            }
          })
          .catch((err) => {
            toast.error(
              `Opportunité créée mais l'export CRM a échoué: ${err.message}`,
            );
          });
      }

      setShowNewOpportunite(false);
      setEditingId(null);
      setNewOpportunite({
        titre: "",
        secteur: "",
        description: "",
        localisation: "",
        departement: "",
        chiffre_affaires: "",
        effectif: "",
        valorisation_min: 0,
        valorisation_max: 0,
        points_forts: "",
        nom_entreprise: "",
        memorandum: "",
        chiffre_affaires_n1: "",
        chiffre_affaires_moyen: "",
        contact: "",
        notes: "",
        cedant_id: "",
      });
      loadData();
    } catch (error) {
      toast.error(
        editingId
          ? "Erreur lors de la mise à jour"
          : "Erreur lors de la création",
      );
    }
  };

  const handleUpdateSettings = async (newData) => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      await axios.patch(`${API}/settings/admin`, newData, { headers });
      setSystemSettings((prev) => ({ ...prev, ...newData }));
      toast.success("Paramètres mis à jour");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour des paramètres");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-xl text-primary animate-pulse">
          Chargement du tableau de bord...
        </p>
      </div>
    );
  }

  const pieData = stats
    ? [
        {
          name: "Premium",
          value: stats.repreneurs_par_niveau.premium,
          color: "hsl(var(--accent))",
        },
        {
          name: "Qualifié",
          value: stats.repreneurs_par_niveau.qualifie,
          color: "hsl(var(--primary))",
        },
        {
          name: "En construction",
          value: stats.repreneurs_par_niveau.en_construction,
          color: "hsl(32 95% 44%)",
        }, // Orange darker
        {
          name: "Curieux",
          value: stats.repreneurs_par_niveau.curieux,
          color: "hsl(var(--muted-foreground))",
        },
      ]
    : [];

  const barData = stats
    ? [
        { name: "Nouveau", value: stats.cedants_par_statut.nouveau },
        { name: "Contacté", value: stats.cedants_par_statut.contacte },
        { name: "Qualifié", value: stats.cedants_par_statut.qualifie },
        { name: "Accompagné", value: stats.cedants_par_statut.accompagne },
      ]
    : [];

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-border/50 px-8 py-6 shadow-sm">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="hover:opacity-80 transition-opacity">
              <span className="text-3xl font-bold text-primary">Sequenti</span>
            </Link>
            <Badge className="bg-primary/10 text-primary border-none text-xs font-bold uppercase tracking-widest px-3 py-1">
              Administration
            </Badge>
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              className="text-foreground/60 hover:text-primary h-12 px-6 font-bold"
              onClick={() =>
                window.open("https://crm.g-digitalpartner.com/", "_blank")
              }
              data-testid="crm-button"
            >
              <ExternalLink className="w-5 h-5 mr-3" />
              CRM
            </Button>
            <Button
              variant="ghost"
              className="text-foreground/60 hover:text-primary h-12 px-6 font-bold"
              onClick={loadData}
              data-testid="refresh-button"
            >
              <RefreshCw className="w-5 h-5 mr-3" />
              Actualiser
            </Button>
            <Button
              variant="ghost"
              className="text-foreground/60 hover:text-primary h-12 px-6 font-bold"
              onClick={() => setShowChangePassword(true)}
              data-testid="security-button"
            >
              <Shield className="w-5 h-5 mr-3" />
              Sécurité
            </Button>
            <Button
              variant="outline"
              className="text-destructive border-destructive/20 hover:bg-destructive/5 h-12 px-6 font-bold"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5 mr-3" />
              Déconnexion
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-8 py-12">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
            {[
              {
                label: "Cédants",
                value: stats.totaux.cedants,
                icon: Building2,
                color: "text-primary",
                bg: "bg-primary/5",
              },
              {
                label: "Repreneurs",
                value: stats.totaux.repreneurs,
                icon: Users,
                color: "text-accent",
                bg: "bg-accent/5",
              },
              {
                label: "Opportunités",
                value: stats.totaux.opportunites,
                icon: Briefcase,
                color: "text-primary",
                bg: "bg-muted",
              },
              {
                label: "Intérêts",
                value: stats.totaux.interets,
                icon: TrendingUp,
                color: "text-accent",
                bg: "bg-accent/5",
              },
            ].map((card, i) => (
              <Card
                key={i}
                className="p-8 border-none bg-white shadow-sm rounded-lg flex items-center gap-6"
              >
                <div
                  className={`w-14 h-14 ${card.bg} rounded-full flex items-center justify-center`}
                >
                  <card.icon className={`w-7 h-7 ${card.color}`} />
                </div>
                <div>
                  <p className="text-3xl font-black text-primary leading-none mb-1">
                    {card.value}
                  </p>
                  <p className="text-sm font-bold uppercase tracking-widest text-foreground/40">
                    {card.label}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Charts */}
        {stats && (
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <Card className="p-8 border-none bg-white shadow-sm rounded-lg">
              <h3 className="text-xl font-bold text-primary mb-8">
                Pipeline Cédants
              </h3>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={barData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="hsl(var(--border))"
                    />
                    <XAxis
                      dataKey="name"
                      fontSize={12}
                      fontWeight={600}
                      stroke="hsl(var(--muted-foreground))"
                    />
                    <YAxis stroke="hsl(var(--muted-foreground))" />
                    <Tooltip
                      cursor={{ fill: "hsl(var(--muted))" }}
                      contentStyle={{
                        borderRadius: "8px",
                        border: "none",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      }}
                    />
                    <Bar
                      dataKey="value"
                      fill="hsl(var(--primary))"
                      radius={[6, 6, 0, 0]}
                      barSize={40}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
            <Card className="p-8 border-none bg-white shadow-sm rounded-lg">
              <h3 className="text-xl font-bold text-primary mb-8">
                Qualification Repreneurs
              </h3>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={70}
                      outerRadius={100}
                      paddingAngle={4}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        borderRadius: "8px",
                        border: "none",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="flex items-center justify-between">
            <TabsList className="bg-muted p-1 h-16 rounded-lg gap-2">
              <TabsTrigger
                value="cedants"
                className="h-full rounded-md px-8 text-base font-bold data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm"
                data-testid="tab-cedants"
              >
                Cédants ({cedants.length})
              </TabsTrigger>
              <TabsTrigger
                value="repreneurs"
                className="h-full rounded-md px-8 text-base font-bold data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm"
                data-testid="tab-repreneurs"
              >
                Repreneurs ({repreneurs.length})
              </TabsTrigger>
              <TabsTrigger
                value="opportunites"
                className="h-full rounded-md px-8 text-base font-bold data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm"
                data-testid="tab-opportunites"
              >
                Opportunités ({opportunites.length})
              </TabsTrigger>
              <TabsTrigger
                value="interets"
                className="h-full rounded-md px-8 text-base font-bold data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm"
                data-testid="tab-interets"
              >
                Intérêts ({interets.length})
              </TabsTrigger>
              <TabsTrigger
                value="users"
                className="h-full rounded-md px-8 text-base font-bold data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm"
                data-testid="tab-users"
              >
                Utilisateurs ({allUsers.length})
              </TabsTrigger>
              <TabsTrigger
                value="recommendations"
                className="h-full rounded-md px-8 text-base font-bold data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm"
                data-testid="tab-recommendations"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Matchs IA{" "}
              </TabsTrigger>
              <TabsTrigger
                value="settings"
                className="h-full rounded-md px-8 text-base font-bold data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm"
                data-testid="tab-settings"
              >
                <Settings className="w-5 h-5 mr-2" />
                Paramètres
              </TabsTrigger>
            </TabsList>
            <Button
              variant="outline"
              className="h-16 px-8 rounded-lg font-bold text-primary border-primary/20 hover:bg-primary/5 shadow-sm"
              onClick={handleExport}
              data-testid="export-button"
            >
              <Download className="w-5 h-5 mr-3" />
            </Button>
          </div>

          {/* Cédants Tab */}
          <TabsContent value="cedants" style={{ width: "1300px" }}>
            <div className="space-y-6">
              {/* Section des cédants en attente */}
              {pendingCedants.length > 0 && (
                <Card className="border-l-4 border-l-yellow-500 shadow-sm rounded-lg overflow-hidden">
                  <div className="p-4 bg-yellow-50 border-b border-yellow-200">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-yellow-600" />
                      <h3 className="font-bold text-yellow-800">
                        Cédants en attente de validation (
                        {pendingCedants.length})
                      </h3>
                      <span className="text-sm text-yellow-600 ml-2">
                        À valider pour activer leurs comptes
                      </span>
                    </div>
                  </div>
                  <Table>
                    <TableHeader className="bg-muted/50">
                      <TableRow>
                        <TableHead className="font-bold text-primary py-4 px-8">
                          Contact
                        </TableHead>
                        <TableHead className="font-bold text-primary py-4">
                          Entreprise
                        </TableHead>
                        <TableHead className="font-bold text-primary py-4">
                          Localisation
                        </TableHead>
                        <TableHead className="font-bold text-primary py-4">
                          Horizon cession
                        </TableHead>
                        <TableHead className="font-bold text-primary py-4">
                          Date d'inscription
                        </TableHead>
                        <TableHead className="font-bold text-primary py-4 px-8 text-right">
                          Actions
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody className="bg-white">
                      {pendingCedants.map((cedant) => (
                        <TableRow
                          key={cedant.id}
                          className="hover:bg-yellow-50/30 transition-colors bg-yellow-50/10"
                        >
                          <TableCell className="px-8">
                            <div>
                              <p className="font-bold text-primary">
                                {cedant.prenom} {cedant.nom || ""}
                              </p>
                              <div className="flex items-center gap-1 text-sm text-foreground/40 mt-1">
                                <Mail className="w-3 h-3" />
                                <span>{cedant.email}</span>
                              </div>
                              {cedant.telephone && (
                                <div className="flex items-center gap-1 text-sm text-foreground/40">
                                  <Phone className="w-3 h-3" />
                                  <span>{cedant.telephone}</span>
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <p className="font-semibold text-primary/80">
                              {cedant.entreprise || "-"}
                            </p>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <MapPin className="w-4 h-4 text-foreground/40" />
                              <span>{cedant.localisation || "-"}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">
                              {cedant.horizon_cession || "-"}
                            </span>
                          </TableCell>
                          <TableCell className="text-sm font-medium text-foreground/40">
                            {formatDate(cedant.created_at)}
                          </TableCell>
                          <TableCell className="px-8 text-right">
                            <div className="flex gap-2 justify-end">
                              <Button
                                variant="default"
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white font-bold"
                                onClick={() =>
                                  handleValidateUser("cedants", cedant.id)
                                }
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                className="bg-red-600 hover:bg-red-700 text-white font-bold"
                                onClick={() =>
                                  handleRejectUser("cedants", cedant.id)
                                }
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              )}

              {/* Section des cédants validés */}
              <Card className="border-none shadow-sm rounded-lg overflow-hidden">
                <div className="p-4 bg-muted/30 border-b border-border">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-primary" />
                    <h3 className="font-bold text-primary">
                      Cédants validés ({cedants.length})
                    </h3>
                  </div>
                </div>
                <Table>
                  <TableHeader className="bg-muted">
                    <TableRow>
                      <TableHead className="font-bold text-primary py-6 px-8">
                        Contact
                      </TableHead>
                      <TableHead className="font-bold text-primary py-6">
                        Entreprise
                      </TableHead>
                      <TableHead className="font-bold text-primary py-6">
                        Valorisation moyenne
                      </TableHead>
                      <TableHead className="font-bold text-primary py-6">
                        Statut
                      </TableHead>
                      <TableHead className="font-bold text-primary py-6">
                        Date
                      </TableHead>
                      <TableHead className="font-bold text-primary py-6 px-8 text-right">
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody className="bg-white">
                    {cedants.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          className="text-center text-foreground/40 py-24 italic text-lg"
                        >
                          Aucun cédant validé
                        </TableCell>
                      </TableRow>
                    ) : (
                      cedants.map((cedant) => (
                        <TableRow
                          key={cedant.id}
                          className="hover:bg-muted/30 transition-colors"
                        >
                          <TableCell className="px-8">
                            <div>
                              <p className="font-bold text-primary">
                                {cedant.prenom} {cedant.nom || ""}
                              </p>
                              <p className="text-sm text-foreground/40">
                                {cedant.email}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-semibold text-primary/80">
                                {cedant.entreprise || "-"}
                              </p>
                              <p className="text-xs text-foreground/40 uppercase font-bold tracking-widest">
                                {cedant.localisation || "-"}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell>
                            {cedant.valuation_estimee ? (
                              <span className="font-black text-accent">
                                {formatCurrency(
                                  cedant.valuation_estimee.moyenne,
                                )}
                              </span>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell>
                            <Select
                              value={cedant.statut}
                              onValueChange={(value) =>
                                handleUpdateStatus("cedants", cedant.id, value)
                              }
                            >
                              <SelectTrigger className="w-32 h-10 rounded-full font-bold text-xs uppercase bg-muted border-none">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {STATUT_CEDANT.map((s) => (
                                  <SelectItem
                                    key={s.value}
                                    value={s.value}
                                    className="font-bold uppercase text-xs"
                                  >
                                    {s.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-sm font-medium text-foreground/40">
                            {formatDate(cedant.created_at)}
                          </TableCell>
                          <TableCell className="px-8 text-right">
                            <div className="flex gap-2 justify-end">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="rounded-full hover:bg-primary/5 text-primary"
                                onClick={() => setSelectedCedant(cedant)}
                              >
                                <Eye className="w-5 h-5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="rounded-full hover:bg-destructive/5 text-destructive"
                                onClick={() =>
                                  handleDeleteItem("cedants", cedant.id)
                                }
                              >
                                <Trash2 className="w-5 h-5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Card>
            </div>
          </TabsContent>

          {/* Repreneurs Tab */}
          <TabsContent value="repreneurs" style={{ width: "1300px" }}>
            <div className="space-y-6">
              {/* Section des repreneurs en attente */}
              {pendingRepreneurs.length > 0 && (
                <Card className="border-l-4 border-l-yellow-500 shadow-sm rounded-lg overflow-hidden">
                  <div className="p-4 bg-yellow-50 border-b border-yellow-200">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-yellow-600" />
                      <h3 className="font-bold text-yellow-800">
                        Repreneurs en attente de validation (
                        {pendingRepreneurs.length})
                      </h3>
                      <span className="text-sm text-yellow-600 ml-2">
                        À valider pour activer leurs comptes
                      </span>
                    </div>
                  </div>
                  <Table>
                    <TableHeader className="bg-muted/50">
                      <TableRow>
                        <TableHead className="font-bold text-primary py-4 px-8">
                          Contact
                        </TableHead>
                        <TableHead className="font-bold text-primary py-4">
                          Budget max
                        </TableHead>
                        <TableHead className="font-bold text-primary py-4">
                          Secteurs ciblés
                        </TableHead>
                        <TableHead className="font-bold text-primary py-4">
                          Expérience
                        </TableHead>
                        <TableHead className="font-bold text-primary py-4">
                          Date d'inscription
                        </TableHead>
                        <TableHead className="font-bold text-primary py-4 px-8 text-right">
                          Actions
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody className="bg-white">
                      {pendingRepreneurs.map((repreneur) => (
                        <TableRow
                          key={repreneur.id}
                          className="hover:bg-yellow-50/30 transition-colors bg-yellow-50/10"
                        >
                          <TableCell className="px-8">
                            <div>
                              <p className="font-bold text-primary">
                                {repreneur.prenom} {repreneur.nom}
                              </p>
                              <div className="flex items-center gap-1 text-sm text-foreground/40 mt-1">
                                <Mail className="w-3 h-3" />
                                <span>{repreneur.email}</span>
                              </div>
                              {repreneur.telephone && (
                                <div className="flex items-center gap-1 text-sm text-foreground/40">
                                  <Phone className="w-3 h-3" />
                                  <span>{repreneur.telephone}</span>
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="font-bold text-accent">
                              {repreneur.qualification?.budget_max || "-"}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {repreneur.qualification?.secteurs_cibles
                                ?.slice(0, 2)
                                .map((s, i) => (
                                  <Badge
                                    key={i}
                                    className="bg-primary/10 text-primary border-none text-[10px] font-bold uppercase"
                                  >
                                    {s}
                                  </Badge>
                                ))}
                              {repreneur.qualification?.secteurs_cibles
                                ?.length > 2 && (
                                <Badge className="bg-muted text-foreground/40 border-none text-[10px] font-bold">
                                  +
                                  {repreneur.qualification.secteurs_cibles
                                    .length - 2}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">
                              {repreneur.qualification?.experience || "-"}
                            </span>
                          </TableCell>
                          <TableCell className="text-sm font-medium text-foreground/40">
                            {formatDate(repreneur.created_at)}
                          </TableCell>

                          <TableCell className="px-8 text-right">
                            <div className="flex gap-2 justify-end">
                              <Button
                                variant="default"
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white font-bold"
                                onClick={() =>
                                  handleValidateUser("repreneurs", repreneur.id)
                                }
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                className="bg-red-600 hover:bg-red-700 text-white font-bold"
                                onClick={() =>
                                  handleRejectUser("repreneurs", repreneur.id)
                                }
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              )}

              {/* Section des repreneurs validés */}
              <Card className="border-none shadow-sm rounded-lg overflow-hidden">
                <div className="p-4 bg-muted/30 border-b border-border">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-accent" />
                    <h3 className="font-bold text-primary">
                      Repreneurs validés ({repreneurs.length})
                    </h3>
                  </div>
                </div>
                <Table>
                  <TableHeader className="bg-muted">
                    <TableRow>
                      <TableHead className="font-bold text-primary py-6 px-8">
                        Contact
                      </TableHead>
                      <TableHead className="font-bold text-primary py-6">
                        Score
                      </TableHead>
                      <TableHead className="font-bold text-primary py-6">
                        Niveau
                      </TableHead>
                      <TableHead className="font-bold text-primary py-6">
                        Secteurs
                      </TableHead>
                      <TableHead className="font-bold text-primary py-6">
                        Statut
                      </TableHead>
                      <TableHead className="font-bold text-primary py-6">
                        Date
                      </TableHead>
                      <TableHead className="font-bold text-primary py-6 px-8 text-right">
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody className="bg-white">
                    {repreneurs.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          className="text-center text-foreground/40 py-24 italic text-lg"
                        >
                          Aucun repreneur validé
                        </TableCell>
                      </TableRow>
                    ) : (
                      repreneurs.map((repreneur) => {
                        const niveauConfig =
                          NIVEAU_CONFIG[repreneur.niveau] ||
                          NIVEAU_CONFIG.curieux;
                        const NiveauIcon = niveauConfig.icon;
                        return (
                          <TableRow
                            key={repreneur.id}
                            className="hover:bg-muted/30 transition-colors"
                          >
                            <TableCell className="px-8">
                              <div>
                                <p className="font-bold text-primary">
                                  {repreneur.prenom} {repreneur.nom}
                                </p>
                                <p className="text-sm text-foreground/40">
                                  {repreneur.email}
                                </p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="font-black text-xl text-primary">
                                {repreneur.score}
                              </span>
                              <span className="text-foreground/20 text-sm font-bold ml-1">
                                /100
                              </span>
                            </TableCell>
                            <TableCell>
                              <div
                                className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${niveauConfig.bg}`}
                              >
                                <NiveauIcon
                                  className={`w-4 h-4 ${niveauConfig.color}`}
                                />
                                <span
                                  className={`text-xs font-bold ${niveauConfig.color} uppercase tracking-wider`}
                                >
                                  {repreneur.niveau.replace("_", " ")}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1 max-w-48">
                                {repreneur.qualification.secteurs_cibles
                                  .slice(0, 2)
                                  .map((s, i) => (
                                    <Badge
                                      key={i}
                                      className="bg-primary/5 text-primary border-none text-[10px] font-bold uppercase"
                                    >
                                      {s}
                                    </Badge>
                                  ))}
                                {repreneur.qualification.secteurs_cibles
                                  .length > 2 && (
                                  <Badge className="bg-muted text-foreground/40 border-none text-[10px] font-bold">
                                    +
                                    {repreneur.qualification.secteurs_cibles
                                      .length - 2}
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Select
                                value={repreneur.statut}
                                onValueChange={(value) =>
                                  handleUpdateStatus(
                                    "repreneurs",
                                    repreneur.id,
                                    value,
                                  )
                                }
                              >
                                <SelectTrigger className="w-32 h-10 rounded-full font-bold text-xs uppercase bg-muted border-none">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {STATUT_REPRENEUR.map((s) => (
                                    <SelectItem
                                      key={s.value}
                                      value={s.value}
                                      className="font-bold uppercase text-xs"
                                    >
                                      {s.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell className="text-sm font-medium text-foreground/40">
                              {formatDate(repreneur.created_at)}
                            </TableCell>
                            <TableCell className="px-8 text-right">
                              <div className="flex gap-2 justify-end">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="rounded-full hover:bg-primary/5 text-primary"
                                  onClick={() =>
                                    setSelectedRepreneur(repreneur)
                                  }
                                >
                                  <Eye className="w-5 h-5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="rounded-full hover:bg-destructive/5 text-destructive"
                                  onClick={() =>
                                    handleDeleteItem("repreneurs", repreneur.id)
                                  }
                                >
                                  <Trash2 className="w-5 h-5" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </Card>
            </div>
          </TabsContent>

          {/* Opportunités Tab */}
          <TabsContent value="opportunites" style={{ width: "1300px" }}>
            <div className="flex justify-end mb-8">
              <Button
                onClick={() => setShowNewOpportunite(true)}
                className="bg-accent text-white hover:bg-accent/90 px-8 h-14 rounded-lg shadow-md font-bold text-lg"
                data-testid="new-opportunite"
              >
                <Plus className="w-6 h-6 mr-3" />
                Nouvelle opportunité
              </Button>
            </div>
            <Card className="border-none shadow-sm rounded-lg overflow-hidden">
              <Table>
                <TableHeader className="bg-muted">
                  <TableRow>
                    <TableHead className="font-bold text-primary py-6 px-8">
                      Titre
                    </TableHead>
                    <TableHead className="font-bold text-primary py-6">
                      Secteur
                    </TableHead>
                    <TableHead className="font-bold text-primary py-6">
                      Localisation
                    </TableHead>
                    <TableHead className="font-bold text-primary py-6">
                      CA
                    </TableHead>
                    <TableHead className="font-bold text-primary py-6">
                      Prix de vente
                    </TableHead>
                    <TableHead className="font-bold text-primary py-6">
                      Statut
                    </TableHead>
                    <TableHead className="font-bold text-primary py-6 px-8 text-right">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody className="bg-white">
                  {opportunites.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="text-center text-foreground/40 py-24 italic text-lg"
                      >
                        Aucune opportunité enregistrée
                      </TableCell>
                    </TableRow>
                  ) : (
                    opportunites.map((opp) => (
                      <TableRow
                        key={opp.id}
                        className="hover:bg-muted/30 transition-colors"
                      >
                        <TableCell className="font-bold text-primary px-8">
                          {opp.titre}
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-primary/5 text-primary border-none text-[10px] font-bold uppercase tracking-wider px-3">
                            {opp.secteur}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium text-foreground/60">
                          {opp.localisation}
                        </TableCell>
                        <TableCell className="font-bold text-primary">
                          {opp.chiffre_affaires}
                        </TableCell>
                        <TableCell className="text-accent font-black">
                          {formatCurrency(opp.valorisation_max)}
                        </TableCell>
                        <TableCell>
                          <Badge
                            className={
                              opp.statut === "active"
                                ? "bg-green-100 text-green-700 border-none"
                                : "bg-muted text-foreground/40 border-none"
                            }
                          >
                            {opp.statut}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-8 text-right">
                          <div className="flex gap-2 justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="rounded-full hover:bg-primary/5 text-primary"
                              onClick={() => handleEdit(opp)}
                            >
                              <Pencil className="w-5 h-5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="rounded-full hover:bg-destructive/5 text-destructive"
                              onClick={() =>
                                handleDeleteItem("opportunites", opp.id)
                              }
                            >
                              <Trash2 className="w-5 h-5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Intérêts Tab */}
          <TabsContent value="interets" style={{ width: "1300px" }}>
            <Card className="border-none shadow-sm rounded-lg overflow-hidden">
              <Table>
                <TableHeader className="bg-muted">
                  <TableRow>
                    <TableHead className="font-bold text-primary py-6 px-8">
                      Repreneur
                    </TableHead>
                    <TableHead className="font-bold text-primary py-6">
                      Opportunité
                    </TableHead>
                    <TableHead className="font-bold text-primary py-6">
                      Message
                    </TableHead>
                    <TableHead className="font-bold text-primary py-6">
                      Date
                    </TableHead>
                    <TableHead className="font-bold text-primary py-6">
                      Statut
                    </TableHead>
                    <TableHead className="font-bold text-primary py-6 px-8 text-right">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody className="bg-white">
                  {interets.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="text-center text-foreground/40 py-24 italic text-lg"
                      >
                        Aucun intérêt manifesté
                      </TableCell>
                    </TableRow>
                  ) : (
                    interets.map((interet) => {
                      const repreneur = allUsers.find(
                        (u) => u.id === interet.repreneur_id,
                      );
                      const opportunite = opportunites.find(
                        (o) => o.id === interet.opportunite_id,
                      );
                      return (
                        <TableRow
                          key={interet.id}
                          className="hover:bg-muted/30 transition-colors"
                        >
                          <TableCell className="px-8 font-bold text-primary">
                            {repreneur
                              ? `${repreneur.prenom} ${repreneur.nom}`
                              : interet.repreneur_id}
                          </TableCell>
                          <TableCell className="font-semibold text-primary/70">
                            {opportunite
                              ? opportunite.titre
                              : interet.opportunite_id}
                          </TableCell>
                          <TableCell className="max-w-xs truncate italic text-foreground/60">
                            "{interet.message || "-"}"
                          </TableCell>
                          <TableCell className="text-sm font-medium text-foreground/40">
                            {formatDate(interet.created_at)}
                          </TableCell>
                          <TableCell>
                            <Badge className="bg-accent/10 text-accent border-none text-xs font-bold uppercase tracking-wider">
                              {interet.statut}
                            </Badge>
                          </TableCell>
                          <TableCell className="px-8 text-right">
                            <div className="flex gap-2 justify-end">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="rounded-full hover:bg-primary/5 text-primary"
                                onClick={() => setSelectedInteret(interet)}
                              >
                                <Eye className="w-5 h-5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="rounded-full hover:bg-primary/5 text-primary"
                                onClick={() => {
                                  setEditInteretData(interet);
                                }}
                              >
                                <Pencil className="w-5 h-5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="rounded-full hover:bg-destructive/5 text-destructive"
                                onClick={() =>
                                  handleDeleteItem("interets", interet.id)
                                }
                              >
                                <Trash2 className="w-5 h-5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Utilisateurs Tab */}
          <TabsContent value="users" style={{ width: "1300px" }}>
            <Card className="border-none shadow-sm rounded-lg overflow-hidden">
              <div className="p-4 bg-muted/30 border-b border-border">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-primary" />
                  <h3 className="font-bold text-primary">
                    Tous les utilisateurs ({allUsers.length})
                  </h3>
                </div>
              </div>
              <Table>
                <TableHeader className="bg-muted">
                  <TableRow>
                    <TableHead className="font-bold text-primary py-6 px-8">
                      Utilisateur
                    </TableHead>
                    <TableHead className="font-bold text-primary py-6">
                      Type
                    </TableHead>
                    <TableHead className="font-bold text-primary py-6">
                      Date Inscription
                    </TableHead>
                    <TableHead className="font-bold text-primary py-6">
                      Validation
                    </TableHead>
                    <TableHead className="font-bold text-primary py-6 px-8 text-right">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody className="bg-white">
                  {allUsers.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="text-center text-foreground/40 py-24 italic text-lg"
                      >
                        Aucun utilisateur trouvé
                      </TableCell>
                    </TableRow>
                  ) : (
                    allUsers.map((user) => {
                      const isRejected = !!user.rejected_at && !user.validation;
                      const isValidated = !!user.validation;
                      const isPending = !user.validation && !user.rejected_at;

                      return (
                        <TableRow
                          key={user.id}
                          className="hover:bg-muted/30 transition-colors"
                        >
                          <TableCell className="px-8">
                            <div>
                              <p className="font-bold text-primary">
                                {user.prenom} {user.nom || ""}
                              </p>
                              <div className="flex items-center gap-1 text-sm text-foreground/40 mt-1">
                                <Mail className="w-3 h-3" />
                                <span>{user.email}</span>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge
                              className={`uppercase text-[10px] font-bold border-none ${
                                user.user_type === "cedant"
                                  ? "bg-blue-100 text-blue-700"
                                  : "bg-purple-100 text-purple-700"
                              }`}
                            >
                              {user.user_type}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm font-medium text-foreground/40">
                            {formatDate(user.created_at)}
                          </TableCell>
                          <TableCell>
                            {isValidated ? (
                              <Badge className="bg-green-100 text-green-700 border-none font-bold">
                                Validé
                              </Badge>
                            ) : isRejected ? (
                              <Badge className="bg-red-100 text-red-700 border-none font-bold">
                                Rejeté
                              </Badge>
                            ) : (
                              <Badge className="bg-yellow-100 text-yellow-700 border-none font-bold">
                                En attente
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="px-8 text-right">
                            <div className="flex gap-2 justify-end">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="rounded-full hover:bg-primary/5 text-primary"
                                onClick={() => {
                                  if (user.user_type === "cedant") {
                                    setSelectedCedant(user);
                                  } else {
                                    setSelectedRepreneur(user);
                                  }
                                }}
                              >
                                <Eye className="w-5 h-5" />
                              </Button>

                              {!isValidated && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="rounded-full hover:bg-green-50 text-green-600"
                                  onClick={() =>
                                    handleValidateUser(user.user_type, user.id)
                                  }
                                  title="Valider"
                                >
                                  <CheckCircle className="w-5 h-5" />
                                </Button>
                              )}

                              {isValidated || isPending ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="rounded-full hover:bg-red-50 text-red-600"
                                  onClick={() =>
                                    handleRejectUser(user.user_type, user.id)
                                  }
                                  title="Rejeter"
                                >
                                  <XCircle className="w-5 h-5" />
                                </Button>
                              ) : null}

                              <Button
                                variant="ghost"
                                size="sm"
                                className="rounded-full hover:bg-destructive/5 text-destructive"
                                onClick={() =>
                                  handleDeleteItem(
                                    user.user_type === "cedant"
                                      ? "cedants"
                                      : "repreneurs",
                                    user.id,
                                  )
                                }
                                title="Supprimer"
                              >
                                <Trash2 className="w-5 h-5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" style={{ width: "1300px" }}>
            <Card className="p-8 border-none bg-white shadow-sm rounded-lg max-w-2xl">
              <h3 className="text-2xl font-bold text-primary mb-8">
                Paramètres du système
              </h3>
              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                  <div className="space-y-1">
                    <Label className="text-base font-bold text-primary">
                      Signature de la NDA obligatoire
                    </Label>
                    <p className="text-sm text-foreground/60">
                      Si activé, les nouveaux repreneurs devront signer une NDA
                      avant validation et recevront le lien par email.
                    </p>
                  </div>
                  <Switch
                    checked={systemSettings.nda_required}
                    onCheckedChange={(checked) =>
                      handleUpdateSettings({ nda_required: checked })
                    }
                  />
                </div>
              </div>
            </Card>
          </TabsContent>
          {/* Recommandations IA Tab */}
          <TabsContent
            value="recommendations"
            style={{ width: "1300px" }}
            data-testid="tab-content-recommendations"
          >
            <div className="space-y-6">
              {/* Header */}
              <Card className="border-l-4 border-l-accent shadow-sm rounded-lg overflow-hidden bg-gradient-to-r from-accent/5 to-transparent">
                <div className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-accent/10 rounded-full">
                      <Sparkles className="w-8 h-8 text-accent" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-primary mb-2">
                        Recommandations IA
                      </h3>
                      <p className="text-foreground/60">
                        L'agent intelligent analyse en continu les profils
                        repreneurs et les opportunités.
                        {groupedRecommendations.length > 0 && (
                          <span className="ml-2 font-bold text-accent">
                            {groupedRecommendations.reduce(
                              (acc, r) => acc + r.opportunities.length,
                              0,
                            )}{" "}
                            match(s) trouvé(s)
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Liste groupée par repreneur */}
              {loadingMatches ? (
                <div className="flex justify-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : groupedRecommendations.length === 0 ? (
                <Card className="border-none shadow-sm rounded-lg p-12 text-center">
                  <Sparkles className="w-16 h-16 text-foreground/20 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-primary mb-2">
                    Aucune recommandation en attente
                  </h3>
                  <p className="text-foreground/60">
                    L'agent analyse les profils en arrière-plan. Revenez plus
                    tard pour voir les suggestions.
                  </p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={loadRecommendations}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Actualiser
                  </Button>
                </Card>
              ) : (
                <div className="space-y-4">
                  {groupedRecommendations.map((repreneur) => {
                    const isExpanded =
                      expandedRepreneurs[repreneur.repreneur_id];
                    const oppCount = repreneur.opportunities.length;
                    const bestScore =
                      repreneur.best_score ||
                      Math.max(
                        ...repreneur.opportunities.map((o) => o.score),
                        0,
                      );

                    return (
                      <Card
                        key={repreneur.repreneur_id}
                        className="border-2 border-border hover:border-accent/30 transition-all shadow-sm rounded-lg overflow-hidden"
                      >
                        {/* En-tête du repreneur - cliquable */}
                        <div
                          className="p-4 bg-gradient-to-r from-primary/5 to-transparent cursor-pointer hover:bg-primary/10 transition-colors"
                          onClick={() =>
                            toggleRepreneurExpand(repreneur.repreneur_id)
                          }
                        >
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-4">
                              <div className="p-2 bg-primary/10 rounded-full">
                                <Users className="w-6 h-6 text-primary" />
                              </div>
                              <div>
                                <h3 className="text-lg font-bold text-primary">
                                  {repreneur.repreneur_nom}
                                </h3>
                                <p className="text-sm text-foreground/60">
                                  {oppCount} opportunité(s) matchée(s) -
                                  Meilleur score: {bestScore}/100
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              {/* Barre de progression globale du repreneur */}
                              <div className="hidden md:block w-32">
                                <div className="flex justify-between text-xs mb-1">
                                  <span className="text-foreground/60">
                                    Fit global
                                  </span>
                                  <span className="font-bold text-accent">
                                    {bestScore}%
                                  </span>
                                </div>
                                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-gradient-to-r from-accent to-accent/70 rounded-full transition-all duration-500"
                                    style={{ width: `${bestScore}%` }}
                                  />
                                </div>
                              </div>
                              <Badge className="bg-accent/10 text-accent border-none">
                                {oppCount} match(s)
                              </Badge>
                              {isExpanded ? (
                                <ChevronUp className="w-5 h-5 text-foreground/40" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-foreground/40" />
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Liste des opportunités - expandable */}
                        {isExpanded && (
                          <div className="border-t border-border p-4">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                              {repreneur.opportunities.map((opp) => {
                                const budgetPoints =
                                  opp.score_details?.budget_points || 0;
                                const locationPoints =
                                  opp.score_details?.location_points || 0;
                                const sectorPoints =
                                  opp.score_details?.sector_points ||
                                  opp.score_details?.sector_point ||
                                  0;

                                return (
                                  <div
                                    key={opp.recommendation_id}
                                    className="border rounded-lg p-4 hover:shadow-md transition-shadow bg-white"
                                  >
                                    {/* Score principal avec anneau ou barre */}
                                    <div className="flex justify-between items-start mb-3">
                                      <div>
                                        <p className="text-xs font-bold uppercase text-foreground/40">
                                          Score de match
                                        </p>
                                        <div className="flex items-baseline gap-2">
                                          <p className="text-3xl font-black text-primary">
                                            {opp.score}
                                            <span className="text-sm font-bold text-foreground/40">
                                              /100
                                            </span>
                                          </p>
                                          {/* Badge de qualité */}
                                          {opp.score >= 90 && (
                                            <Badge className="bg-green-100 text-green-700 border-green-200">
                                              Excellent
                                            </Badge>
                                          )}
                                          {opp.score >= 80 &&
                                            opp.score < 90 && (
                                              <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                                                Très bien
                                              </Badge>
                                            )}
                                          {opp.score >= 70 &&
                                            opp.score < 80 && (
                                              <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">
                                                Bon
                                              </Badge>
                                            )}
                                        </div>
                                      </div>
                                      <Badge className="bg-primary/10 text-primary border-none max-w-[150px] truncate">
                                        {opp.opportunite_titre?.substring(
                                          0,
                                          20,
                                        )}
                                        {opp.opportunite_titre?.length > 20
                                          ? "..."
                                          : ""}
                                      </Badge>
                                    </div>

                                    {/* Barre de progression principale */}
                                    <div className="mb-4">
                                      <div className="flex justify-between text-xs mb-1">
                                        <span className="text-foreground/60">
                                          Match global
                                        </span>
                                        <span className="font-bold text-accent">
                                          {opp.score}%
                                        </span>
                                      </div>
                                      <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                                        <div
                                          className={`h-full rounded-full transition-all duration-700 ${
                                            opp.score >= 90
                                              ? "bg-gradient-to-r from-green-500 to-green-400"
                                              : opp.score >= 80
                                                ? "bg-gradient-to-r from-blue-500 to-blue-400"
                                                : opp.score >= 70
                                                  ? "bg-gradient-to-r from-yellow-500 to-yellow-400"
                                                  : "bg-gradient-to-r from-gray-500 to-gray-400"
                                          }`}
                                          style={{ width: `${opp.score}%` }}
                                        />
                                      </div>
                                    </div>

                                    {/* Détails du score avec barres */}
                                    <div className="mb-3">
                                      <p className="text-xs font-bold text-primary mb-2">
                                        Détails du match
                                      </p>
                                      <div className="space-y-2 text-sm">
                                        {/* Budget */}
                                        <div>
                                          <div className="flex justify-between mb-0.5">
                                            <span className="text-foreground/60">
                                              Budget
                                            </span>
                                            <span className="font-bold text-accent text-xs">
                                              {budgetPoints}/40
                                            </span>
                                          </div>
                                          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                            <div
                                              className="h-full bg-accent rounded-full transition-all duration-500"
                                              style={{
                                                width: `${(budgetPoints / 40) * 100}%`,
                                              }}
                                            />
                                          </div>
                                        </div>
                                        {/* Localisation */}
                                        <div>
                                          <div className="flex justify-between mb-0.5">
                                            <span className="text-foreground/60">
                                              Localisation
                                            </span>
                                            <span className="font-bold text-accent text-xs">
                                              {locationPoints}/30
                                            </span>
                                          </div>
                                          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                            <div
                                              className="h-full bg-accent rounded-full transition-all duration-500"
                                              style={{
                                                width: `${(locationPoints / 30) * 100}%`,
                                              }}
                                            />
                                          </div>
                                        </div>
                                        {/* Secteur */}
                                        <div>
                                          <div className="flex justify-between mb-0.5">
                                            <span className="text-foreground/60">
                                              Secteur
                                            </span>
                                            <span className="font-bold text-accent text-xs">
                                              {sectorPoints}/20
                                            </span>
                                          </div>
                                          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                            <div
                                              className="h-full bg-accent rounded-full transition-all duration-500"
                                              style={{
                                                width: `${(sectorPoints / 20) * 100}%`,
                                              }}
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Raison du match (match_reason) */}
                                    {opp.match_reason && (
                                      <div className="mb-3 p-2 bg-accent/5 rounded-lg border border-accent/10">
                                        <p className="text-xs font-bold text-accent mb-1">
                                          📊 Pourquoi ce match ?
                                        </p>
                                        <p className="text-xs text-foreground/70 leading-relaxed">
                                          {opp.match_reason}
                                        </p>
                                      </div>
                                    )}

                                    {/* Boutons d'action */}
                                    <div className="flex gap-2 mt-2">
                                      <Button
                                        size="sm"
                                        className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleAcceptRecommendation({
                                            recommendation_id:
                                              opp.recommendation_id,
                                            repreneur_id:
                                              repreneur.repreneur_id,
                                            repreneur_nom:
                                              repreneur.repreneur_nom,
                                            opportunite_id: opp.opportunite_id,
                                            score: opp.score,
                                          });
                                        }}
                                        disabled={
                                          processingRecommendation ===
                                          opp.recommendation_id
                                        }
                                      >
                                        {processingRecommendation ===
                                        opp.recommendation_id ? (
                                          <RefreshCw className="w-4 h-4 animate-spin" />
                                        ) : (
                                          <Check className="w-4 h-4 mr-1" />
                                        )}
                                        Accepter
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleSkipRecommendation({
                                            recommendation_id:
                                              opp.recommendation_id,
                                            repreneur_id:
                                              repreneur.repreneur_id,
                                            repreneur_nom:
                                              repreneur.repreneur_nom,
                                            opportunite_id: opp.opportunite_id,
                                          });
                                        }}
                                        disabled={
                                          processingRecommendation ===
                                          opp.recommendation_id
                                        }
                                      >
                                        <SkipForward className="w-4 h-4 mr-1" />
                                        Ignorer
                                      </Button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* New/Edit Opportunité Dialog */}
      <Dialog
        open={showNewOpportunite}
        onOpenChange={(open) => {
          if (!open) {
            setEditingId(null);
            setNewOpportunite({
              titre: "",
              secteur: "",
              description: "",
              localisation: "",
              departement: "",
              chiffre_affaires: "",
              effectif: "",
              valorisation_min: 0,
              valorisation_max: 0,
              points_forts: "",
              nom_entreprise: "",
              memorandum: "",
              chiffre_affaires_n1: "",
              chiffre_affaires_moyen: "",
              contact: "",
              notes: "",
              cedant_id: "",
            });
          }
          setShowNewOpportunite(open);
        }}
      >
        <DialogContent className="max-w-5xl p-8 rounded-lg">
          <DialogHeader className="mb-4">
            <DialogTitle className="text-3xl font-black text-primary">
              {editingId ? "Modifier l'opportunité" : "Nouvelle opportunité"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2 overflow-y-auto max-h-[70vh] pr-2">
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-8 space-y-2">
                <Label className="text-base font-bold text-primary">
                  Titre*
                </Label>
                <Input
                  className="h-10 text-base rounded-lg border-border/50"
                  value={newOpportunite.titre}
                  onChange={(e) =>
                    setNewOpportunite((prev) => ({
                      ...prev,
                      titre: e.target.value,
                    }))
                  }
                  placeholder="PME industrielle - Secteur"
                  data-testid="opp-titre"
                />
              </div>
              <div className="col-span-4 space-y-2">
                <Label className="text-base font-bold text-primary">
                  Secteur*
                </Label>
                <Select
                  value={newOpportunite.secteur}
                  onValueChange={(value) =>
                    setNewOpportunite((prev) => ({ ...prev, secteur: value }))
                  }
                >
                  <SelectTrigger
                    className="h-10 text-base rounded-lg border-border/50"
                    data-testid="opp-secteur"
                  >
                    <SelectValue placeholder="Choisir un secteur" />
                  </SelectTrigger>
                  <SelectContent>
                    {SECTEURS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-6 space-y-2">
                <Label className="text-base font-bold text-primary">
                  Localisation*
                </Label>
                <Input
                  className="h-10 text-base rounded-lg border-border/50"
                  value={newOpportunite.localisation}
                  onChange={(e) =>
                    setNewOpportunite((prev) => ({
                      ...prev,
                      localisation: e.target.value,
                    }))
                  }
                  placeholder="Ville ou Région"
                  data-testid="opp-localisation"
                />
              </div>
              <div className="col-span-3 space-y-2">
                <Label className="text-base font-bold text-primary">
                  Chiffre d'affaires*
                </Label>
                <Input
                  type="number"
                  className="h-10 text-base rounded-lg border-border/50"
                  value={newOpportunite.chiffre_affaires}
                  onChange={(e) =>
                    setNewOpportunite((prev) => ({
                      ...prev,
                      chiffre_affaires: e.target.value,
                    }))
                  }
                  placeholder="ex: 1500000"
                  data-testid="opp-ca"
                />
              </div>
              <div className="col-span-3 space-y-2">
                <Label className="text-base font-bold text-primary">
                  Effectif*
                </Label>
                <Input
                  className="h-10 text-base rounded-lg border-border/50"
                  value={newOpportunite.effectif}
                  onChange={(e) =>
                    setNewOpportunite((prev) => ({
                      ...prev,
                      effectif: e.target.value,
                    }))
                  }
                  placeholder="ex: 12 salariés"
                  data-testid="opp-effectif"
                />
              </div>
            </div>

            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-3 space-y-2">
                <Label className="text-base font-bold text-primary">
                  Prix de vente* (€)
                </Label>
                <Input
                  type="number"
                  className="h-10 text-base rounded-lg border-border/50"
                  value={newOpportunite.valorisation_max}
                  onChange={(e) =>
                    setNewOpportunite((prev) => ({
                      ...prev,
                      valorisation_max: e.target.value,
                    }))
                  }
                  placeholder="500000"
                  data-testid="opp-valo-max"
                />
              </div>
              <div className="col-span-9 space-y-2">
                <Label className="text-base font-bold text-primary">
                  Lien teaser*
                </Label>
                <Input
                  className="h-10 text-base rounded-lg border-border/50"
                  value={newOpportunite.memorandum}
                  onChange={(e) =>
                    setNewOpportunite((prev) => ({
                      ...prev,
                      memorandum: e.target.value,
                    }))
                  }
                  placeholder="URL du document"
                />
              </div>
            </div>

            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-12 space-y-2">
                <Label className="text-base font-bold text-primary">
                  Nom réel de l'entreprise
                </Label>
                <Input
                  className="h-10 text-base rounded-lg border-border/50"
                  value={newOpportunite.nom_entreprise}
                  onChange={(e) =>
                    setNewOpportunite((prev) => ({
                      ...prev,
                      nom_entreprise: e.target.value,
                    }))
                  }
                  placeholder="ex: Transports Martin SAS"
                />
              </div>
            </div>

            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-6 space-y-2">
                <Label className="text-base font-bold text-primary">
                  CA N-1
                </Label>
                <Input
                  className="h-10 text-base rounded-lg border-border/50"
                  value={newOpportunite.chiffre_affaires_n1}
                  onChange={(e) =>
                    setNewOpportunite((prev) => ({
                      ...prev,
                      chiffre_affaires_n1: e.target.value,
                    }))
                  }
                  placeholder="ex: 1 400 000€"
                />
              </div>
              <div className="col-span-6 space-y-2">
                <Label className="text-base font-bold text-primary">
                  CA Moyen (3 ans)
                </Label>
                <Input
                  className="h-10 text-base rounded-lg border-border/50"
                  value={newOpportunite.chiffre_affaires_moyen}
                  onChange={(e) =>
                    setNewOpportunite((prev) => ({
                      ...prev,
                      chiffre_affaires_moyen: e.target.value,
                    }))
                  }
                  placeholder="ex: 1 500 000€"
                />
              </div>
            </div>

            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-6 space-y-2">
                <Label className="text-base font-bold text-primary">
                  Contact (Nom/Email/Tel)
                </Label>
                <Input
                  className="h-10 text-base rounded-lg border-border/50"
                  value={newOpportunite.contact}
                  onChange={(e) =>
                    setNewOpportunite((prev) => ({
                      ...prev,
                      contact: e.target.value,
                    }))
                  }
                  placeholder="Coordonnées de contact direct"
                />
              </div>
              <div className="col-span-6 space-y-2">
                <Label className="text-base font-bold text-primary">
                  Notes internes
                </Label>
                <Input
                  className="h-10 text-base rounded-lg border-border/50"
                  value={newOpportunite.notes}
                  onChange={(e) =>
                    setNewOpportunite((prev) => ({
                      ...prev,
                      notes: e.target.value,
                    }))
                  }
                  placeholder="Informations réservées à l'admin"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-base font-bold text-primary">
                Description détaillée*
              </Label>
              <Textarea
                className="text-base rounded-lg border-border/50 p-3"
                value={newOpportunite.description}
                onChange={(e) =>
                  setNewOpportunite((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder="Décrivez l'entreprise en quelques lignes..."
                rows={3}
                data-testid="opp-description"
              />
            </div>
            <Label className="text-base font-bold text-primary">
              Les champs publics sont indiqués par la mention *
            </Label>
          </div>
          <DialogFooter className="mt-6 gap-4">
            <Button
              variant="ghost"
              className="h-10 px-8 text-base"
              onClick={() => {
                setShowNewOpportunite(false);
                setEditingId(null);
                setNewOpportunite({
                  titre: "",
                  secteur: "",
                  description: "",
                  localisation: "",
                  departement: "",
                  chiffre_affaires: "",
                  effectif: "",
                  valorisation_min: 0,
                  valorisation_max: 0,
                  points_forts: "",
                  nom_entreprise: "",
                  memorandum: "",
                  chiffre_affaires_n1: "",
                  chiffre_affaires_moyen: "",
                  contact: "",
                  notes: "",
                  cedant_id: "",
                });
              }}
            >
              Annuler
            </Button>
            <Button
              onClick={handleCreateOpportunite}
              className="bg-accent text-white hover:bg-accent/90 h-10 px-12 text-base font-bold rounded-lg shadow-md"
              data-testid="create-opportunite"
            >
              {editingId ? "Mettre à jour" : "Créer l'opportunité"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cedant Detail Dialog - Editable Version */}
      <Dialog
        open={!!selectedCedant}
        onOpenChange={() => setSelectedCedant(null)}
      >
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto p-10 rounded-lg">
          <DialogHeader className="mb-8">
            <DialogTitle className="text-3xl font-black text-primary">
              Détail du cédant
            </DialogTitle>
          </DialogHeader>
          {editCedantData && (
            <div className="space-y-8">
              <div className="grid grid-cols-2 gap-6 bg-muted p-6 rounded-lg">
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-foreground/40">
                    Prénom
                  </Label>
                  <Input
                    value={editCedantData.prenom}
                    onChange={(e) =>
                      setEditCedantData({
                        ...editCedantData,
                        prenom: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-foreground/40">
                    Nom
                  </Label>
                  <Input
                    value={editCedantData.nom || ""}
                    onChange={(e) =>
                      setEditCedantData({
                        ...editCedantData,
                        nom: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-foreground/40">
                    Email
                  </Label>
                  <Input
                    value={editCedantData.email}
                    onChange={(e) =>
                      setEditCedantData({
                        ...editCedantData,
                        email: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-foreground/40">
                    Téléphone
                  </Label>
                  <Input
                    value={editCedantData.telephone || ""}
                    onChange={(e) =>
                      setEditCedantData({
                        ...editCedantData,
                        telephone: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-foreground/40">
                    Statut
                  </Label>
                  <Select
                    value={editCedantData.statut}
                    onValueChange={(value) =>
                      setEditCedantData({ ...editCedantData, statut: value })
                    }
                  >
                    <SelectTrigger className="bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUT_CEDANT.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-foreground/40">
                    Validation
                  </Label>
                  <Select
                    value={editCedantData.validation ? "true" : "false"}
                    onValueChange={(value) =>
                      setEditCedantData({
                        ...editCedantData,
                        validation: value === "true",
                      })
                    }
                  >
                    <SelectTrigger className="bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">Validé</SelectItem>
                      <SelectItem value="false">Non validé</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <Card className="p-6 border-none bg-white shadow-sm border border-border/40">
                  <p className="text-xs font-bold uppercase tracking-widest text-foreground/40 mb-6 border-b border-border/50 pb-2">
                    Simulation (Valuation)
                  </p>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-xs font-bold text-primary/40 uppercase">
                        Secteur
                      </Label>
                      <Input
                        value={editCedantData.valuation.secteur || ""}
                        onChange={(e) =>
                          setEditCedantData({
                            ...editCedantData,
                            valuation: {
                              ...editCedantData.valuation,
                              secteur: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-bold text-primary/40 uppercase">
                        Chiffre d'affaires
                      </Label>
                      <Input
                        value={editCedantData.valuation.chiffre_affaires || ""}
                        onChange={(e) =>
                          setEditCedantData({
                            ...editCedantData,
                            valuation: {
                              ...editCedantData.valuation,
                              chiffre_affaires: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-bold text-primary/40 uppercase">
                        Résultat Net
                      </Label>
                      <Input
                        value={editCedantData.valuation.resultat_net || ""}
                        onChange={(e) =>
                          setEditCedantData({
                            ...editCedantData,
                            valuation: {
                              ...editCedantData.valuation,
                              resultat_net: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-bold text-primary/40 uppercase">
                        Effectif
                      </Label>
                      <Input
                        value={editCedantData.valuation.effectif || ""}
                        onChange={(e) =>
                          setEditCedantData({
                            ...editCedantData,
                            valuation: {
                              ...editCedantData.valuation,
                              effectif: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-bold text-primary/40 uppercase">
                        Ancienneté
                      </Label>
                      <Input
                        value={editCedantData.valuation.anciennete || ""}
                        onChange={(e) =>
                          setEditCedantData({
                            ...editCedantData,
                            valuation: {
                              ...editCedantData.valuation,
                              anciennete: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                  </div>
                </Card>
                <Card className="p-6 border-none bg-white shadow-sm border border-border/40">
                  <p className="text-xs font-bold uppercase tracking-widest text-foreground/40 mb-6 border-b border-border/50 pb-2">
                    Entreprise & Projet
                  </p>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-xs font-bold text-primary/40 uppercase">
                        Nom Entreprise
                      </Label>
                      <Input
                        value={editCedantData.entreprise || ""}
                        onChange={(e) =>
                          setEditCedantData({
                            ...editCedantData,
                            entreprise: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-bold text-primary/40 uppercase">
                        Localisation
                      </Label>
                      <Input
                        value={editCedantData.localisation || ""}
                        onChange={(e) =>
                          setEditCedantData({
                            ...editCedantData,
                            localisation: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-bold text-primary/40 uppercase">
                        Horizon Cession
                      </Label>
                      <Input
                        value={editCedantData.horizon_cession || ""}
                        onChange={(e) =>
                          setEditCedantData({
                            ...editCedantData,
                            horizon_cession: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-bold text-primary/40 uppercase">
                        Motivation
                      </Label>
                      <Textarea
                        value={editCedantData.motivation || ""}
                        onChange={(e) =>
                          setEditCedantData({
                            ...editCedantData,
                            motivation: e.target.value,
                          })
                        }
                        rows={3}
                      />
                    </div>
                  </div>
                </Card>
              </div>

              <div className="grid grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-foreground/40">
                    Secteur précis
                  </Label>
                  <Input
                    value={editCedantData.secteur_activite || ""}
                    onChange={(e) =>
                      setEditCedantData({
                        ...editCedantData,
                        secteur_activite: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-foreground/40">
                    CA N-1
                  </Label>
                  <Input
                    value={editCedantData.chiffre_affaires_n1 || ""}
                    onChange={(e) =>
                      setEditCedantData({
                        ...editCedantData,
                        chiffre_affaires_n1: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-foreground/40">
                    CA Moyen
                  </Label>
                  <Input
                    value={editCedantData.chiffre_affaires_moyen || ""}
                    onChange={(e) =>
                      setEditCedantData({
                        ...editCedantData,
                        chiffre_affaires_moyen: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-foreground/40">
                    Contact (Admin)
                  </Label>
                  <Input
                    value={editCedantData.contact || ""}
                    onChange={(e) =>
                      setEditCedantData({
                        ...editCedantData,
                        contact: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-foreground/40">
                    Notes internes
                  </Label>
                  <Textarea
                    value={editCedantData.notes || ""}
                    onChange={(e) =>
                      setEditCedantData({
                        ...editCedantData,
                        notes: e.target.value,
                      })
                    }
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-foreground/40">
                    Teaser
                  </Label>
                  <Textarea
                    value={editCedantData.memorandum || ""}
                    onChange={(e) =>
                      setEditCedantData({
                        ...editCedantData,
                        memorandum: e.target.value,
                      })
                    }
                    rows={3}
                  />
                </div>
              </div>

              {editCedantData.valuation_estimee && (
                <Card className="p-8 bg-primary text-white border-none rounded-lg shadow-lg relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-12 -mt-12" />
                  <p className="text-sm font-bold uppercase tracking-widest mb-4 opacity-60">
                    Valorisation estimée (Lecture seule)
                  </p>
                  <p className="text-5xl font-black mb-4">
                    {formatCurrency(editCedantData.valuation_estimee.moyenne)}
                  </p>
                  <div className="flex justify-between items-center pt-4 border-t border-white/10 text-sm font-bold">
                    <span>
                      Fourchette basse:{" "}
                      {formatCurrency(
                        editCedantData.valuation_estimee.fourchette_basse,
                      )}
                    </span>
                    <span className="text-accent">
                      Fourchette haute:{" "}
                      {formatCurrency(
                        editCedantData.valuation_estimee.fourchette_haute,
                      )}
                    </span>
                  </div>
                </Card>
              )}
            </div>
          )}
          <DialogFooter className="mt-8 flex justify-between gap-4">
            <Button variant="ghost" onClick={() => setSelectedCedant(null)}>
              Annuler
            </Button>
            <Button
              onClick={handleUpdateCedant}
              className="bg-accent text-white hover:bg-accent/90"
            >
              Enregistrer les modifications
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Repreneur Detail Dialog - Editable Version */}
      <Dialog
        open={!!selectedRepreneur}
        onOpenChange={() => setSelectedRepreneur(null)}
      >
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto p-10 rounded-lg">
          <DialogHeader className="mb-8">
            <DialogTitle className="text-3xl font-black text-primary">
              Détail du repreneur
            </DialogTitle>
          </DialogHeader>
          {editRepreneurData && (
            <div className="space-y-8">
              <div className="grid grid-cols-2 gap-6 bg-muted p-6 rounded-lg">
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-foreground/40">
                    Prénom
                  </Label>
                  <Input
                    value={editRepreneurData.prenom}
                    onChange={(e) =>
                      setEditRepreneurData({
                        ...editRepreneurData,
                        prenom: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-foreground/40">
                    Nom
                  </Label>
                  <Input
                    value={editRepreneurData.nom}
                    onChange={(e) =>
                      setEditRepreneurData({
                        ...editRepreneurData,
                        nom: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-foreground/40">
                    Email
                  </Label>
                  <Input
                    value={editRepreneurData.email}
                    onChange={(e) =>
                      setEditRepreneurData({
                        ...editRepreneurData,
                        email: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-foreground/40">
                    Téléphone
                  </Label>
                  <Input
                    value={editRepreneurData.telephone || ""}
                    onChange={(e) =>
                      setEditRepreneurData({
                        ...editRepreneurData,
                        telephone: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-foreground/40">
                    Statut
                  </Label>
                  <Select
                    value={editRepreneurData.statut}
                    onValueChange={(value) =>
                      setEditRepreneurData({
                        ...editRepreneurData,
                        statut: value,
                      })
                    }
                  >
                    <SelectTrigger className="bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUT_REPRENEUR.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-foreground/40">
                    Validation
                  </Label>
                  <Select
                    value={editRepreneurData.validation ? "true" : "false"}
                    onValueChange={(value) =>
                      setEditRepreneurData({
                        ...editRepreneurData,
                        validation: value === "true",
                      })
                    }
                  >
                    <SelectTrigger className="bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">Validé</SelectItem>
                      <SelectItem value="false">Non validé</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <Card className="p-6 border-none bg-white shadow-sm border border-border/40">
                  <p className="text-xs font-bold uppercase tracking-widest text-foreground/40 mb-6 border-b border-border/50 pb-2">
                    Qualification
                  </p>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-xs font-bold text-primary/40 uppercase">
                        Capacité d'apport
                      </Label>
                      <Select
                        value={
                          editRepreneurData.qualification.capacite_financiere
                        }
                        onValueChange={(value) =>
                          setEditRepreneurData({
                            ...editRepreneurData,
                            qualification: {
                              ...editRepreneurData.qualification,
                              capacite_financiere: value,
                            },
                          })
                        }
                      >
                        <SelectTrigger className="bg-muted">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CAPACITE_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-bold text-primary/40 uppercase">
                        Expérience
                      </Label>
                      <Select
                        value={editRepreneurData.qualification.experience}
                        onValueChange={(value) =>
                          setEditRepreneurData({
                            ...editRepreneurData,
                            qualification: {
                              ...editRepreneurData.qualification,
                              experience: value,
                            },
                          })
                        }
                      >
                        <SelectTrigger className="bg-muted">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {EXPERIENCE_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-bold text-primary/40 uppercase">
                        Budget Max
                      </Label>
                      <Select
                        value={editRepreneurData.qualification.budget_max}
                        onValueChange={(value) =>
                          setEditRepreneurData({
                            ...editRepreneurData,
                            qualification: {
                              ...editRepreneurData.qualification,
                              budget_max: value,
                            },
                          })
                        }
                      >
                        <SelectTrigger className="bg-muted">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {BUDGET_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </Card>
                <Card className="p-6 border-none bg-white shadow-sm border border-border/40">
                  <p className="text-xs font-bold uppercase tracking-widest text-foreground/40 mb-6 border-b border-border/50 pb-2">
                    Projet & Dispo
                  </p>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-xs font-bold text-primary/40 uppercase">
                        Horizon projet
                      </Label>
                      <Select
                        value={editRepreneurData.qualification.horizon_projet}
                        onValueChange={(value) =>
                          setEditRepreneurData({
                            ...editRepreneurData,
                            qualification: {
                              ...editRepreneurData.qualification,
                              horizon_projet: value,
                            },
                          })
                        }
                      >
                        <SelectTrigger className="bg-muted">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {HORIZON_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-bold text-primary/40 uppercase">
                        Accord bancaire
                      </Label>
                      <Select
                        value={
                          editRepreneurData.qualification.pre_accord_bancaire
                        }
                        onValueChange={(value) =>
                          setEditRepreneurData({
                            ...editRepreneurData,
                            qualification: {
                              ...editRepreneurData.qualification,
                              pre_accord_bancaire: value,
                            },
                          })
                        }
                      >
                        <SelectTrigger className="bg-muted">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ACCORD_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-bold text-primary/40 uppercase">
                        Disponibilité
                      </Label>
                      <Select
                        value={editRepreneurData.qualification.disponibilite}
                        onValueChange={(value) =>
                          setEditRepreneurData({
                            ...editRepreneurData,
                            qualification: {
                              ...editRepreneurData.qualification,
                              disponibilite: value,
                            },
                          })
                        }
                      >
                        <SelectTrigger className="bg-muted">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DISPONIBILITE_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-bold text-primary/40 uppercase">
                        Accompagnement
                      </Label>
                      <Select
                        value={
                          editRepreneurData.qualification
                            .accompagnement_souhaite
                        }
                        onValueChange={(value) =>
                          setEditRepreneurData({
                            ...editRepreneurData,
                            qualification: {
                              ...editRepreneurData.qualification,
                              accompagnement_souhaite: value,
                            },
                          })
                        }
                      >
                        <SelectTrigger className="bg-muted">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ACCOMPAGNEMENT_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </Card>
              </div>

              <div className="grid grid-cols-2 gap-8">
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-foreground/40">
                    Secteurs ciblés (séparés par virgule)
                  </Label>
                  <Input
                    value={
                      Array.isArray(
                        editRepreneurData.qualification.secteurs_cibles,
                      )
                        ? editRepreneurData.qualification.secteurs_cibles.join(
                            ", ",
                          )
                        : editRepreneurData.qualification.secteurs_cibles
                    }
                    onChange={(e) =>
                      setEditRepreneurData({
                        ...editRepreneurData,
                        qualification: {
                          ...editRepreneurData.qualification,
                          secteurs_cibles: e.target.value,
                        },
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-foreground/40">
                    Zones géo (séparées par virgule)
                  </Label>
                  <Input
                    value={
                      Array.isArray(
                        editRepreneurData.qualification.zones_geographiques,
                      )
                        ? editRepreneurData.qualification.zones_geographiques.join(
                            ", ",
                          )
                        : editRepreneurData.qualification.zones_geographiques
                    }
                    onChange={(e) =>
                      setEditRepreneurData({
                        ...editRepreneurData,
                        qualification: {
                          ...editRepreneurData.qualification,
                          zones_geographiques: e.target.value,
                        },
                      })
                    }
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-foreground/40">
                    Secteur actuel
                  </Label>
                  <Input
                    value={editRepreneurData.secteur_activite || ""}
                    onChange={(e) =>
                      setEditRepreneurData({
                        ...editRepreneurData,
                        secteur_activite: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-foreground/40">
                    CA N-1
                  </Label>
                  <Input
                    value={editRepreneurData.chiffre_affaires_n1 || ""}
                    onChange={(e) =>
                      setEditRepreneurData({
                        ...editRepreneurData,
                        chiffre_affaires_n1: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-foreground/40">
                    CA Moyen
                  </Label>
                  <Input
                    value={editRepreneurData.chiffre_affaires_moyen || ""}
                    onChange={(e) =>
                      setEditRepreneurData({
                        ...editRepreneurData,
                        chiffre_affaires_moyen: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-foreground/40">
                    Contact
                  </Label>
                  <Input
                    value={editRepreneurData.contact || ""}
                    onChange={(e) =>
                      setEditRepreneurData({
                        ...editRepreneurData,
                        contact: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-foreground/40">
                    Notes internes
                  </Label>
                  <Textarea
                    value={editRepreneurData.notes || ""}
                    onChange={(e) =>
                      setEditRepreneurData({
                        ...editRepreneurData,
                        notes: e.target.value,
                      })
                    }
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-foreground/40">
                    Teaser
                  </Label>
                  <Textarea
                    value={editRepreneurData.memorandum || ""}
                    onChange={(e) =>
                      setEditRepreneurData({
                        ...editRepreneurData,
                        memorandum: e.target.value,
                      })
                    }
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-foreground/40">
                    Motivation (client)
                  </Label>
                  <Textarea
                    value={editRepreneurData.qualification.motivation || ""}
                    onChange={(e) =>
                      setEditRepreneurData({
                        ...editRepreneurData,
                        qualification: {
                          ...editRepreneurData.qualification,
                          motivation: e.target.value,
                        },
                      })
                    }
                    rows={3}
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="mt-8 flex justify-between gap-4">
            <Button variant="ghost" onClick={() => setSelectedRepreneur(null)}>
              Annuler
            </Button>
            <Button
              onClick={handleUpdateRepreneur}
              className="bg-accent text-white hover:bg-accent/90"
            >
              Enregistrer les modifications
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Interest Detail Dialog */}
      <Dialog
        open={!!selectedInteret}
        onOpenChange={() => setSelectedInteret(null)}
      >
        <DialogContent className="sm:max-w-2xl p-10 rounded-lg">
          <DialogHeader className="mb-8">
            <DialogTitle className="text-3xl font-black text-primary">
              Détail de l'intérêt
            </DialogTitle>
          </DialogHeader>
          {selectedInteret && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-xs font-bold uppercase text-foreground/40 mb-2">
                    Repreneur
                  </p>
                  {(() => {
                    const r = allUsers.find(
                      (u) => u.id === selectedInteret.repreneur_id,
                    );
                    return r ? (
                      <>
                        <p className="font-bold text-primary">
                          {r.prenom} {r.nom}
                        </p>
                        <p className="text-sm text-foreground/60">{r.email}</p>
                        <p className="text-sm text-foreground/60">
                          {r.telephone}
                        </p>
                      </>
                    ) : (
                      <p className="text-sm italic">
                        Repreneur non trouvé ({selectedInteret.repreneur_id})
                      </p>
                    );
                  })()}
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-xs font-bold uppercase text-foreground/40 mb-2">
                    Opportunité
                  </p>
                  {(() => {
                    const o = opportunites.find(
                      (opp) => opp.id === selectedInteret.opportunite_id,
                    );
                    return o ? (
                      <>
                        <p className="font-bold text-primary">{o.titre}</p>
                        <p className="text-sm text-foreground/60">
                          {o.secteur} - {o.localisation}
                        </p>
                        <p className="text-sm font-bold text-accent mt-1">
                          {o.chiffre_affaires}
                        </p>
                      </>
                    ) : (
                      <p className="text-sm italic">
                        Opportunité non trouvée (
                        {selectedInteret.opportunite_id})
                      </p>
                    );
                  })()}
                </div>
              </div>
              <div className="p-4 border border-border rounded-lg">
                <p className="text-xs font-bold uppercase text-foreground/40 mb-2">
                  Message du repreneur
                </p>
                <p className="italic text-foreground/80">
                  "{selectedInteret.message || "Aucun message"}"
                </p>
              </div>
              <div className="flex justify-between items-center px-4 py-2 bg-primary/5 rounded-lg">
                <span className="text-sm font-bold text-primary">
                  Statut actuel:
                </span>
                <Badge className="bg-accent/10 text-accent border-none text-xs font-bold uppercase tracking-wider">
                  {selectedInteret.statut}
                </Badge>
              </div>
            </div>
          )}
          <DialogFooter className="mt-8">
            <Button variant="ghost" onClick={() => setSelectedInteret(null)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog
        open={showChangePassword}
        onOpenChange={(open) => {
          if (!open) {
            setChangePasswordData({
              ancien_mot_de_passe: "",
              nouveau_mot_de_passe: "",
              confirm_mot_de_passe: "",
            });
          }
          setShowChangePassword(open);
        }}
      >
        <DialogContent className="sm:max-w-md p-10 rounded-lg">
          <DialogHeader className="mb-8">
            <DialogTitle className="text-3xl font-black text-primary">
              Sécurité du compte
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-foreground/40">
                Mot de passe actuel
              </Label>
              <Input
                type="password"
                value={changePasswordData.ancien_mot_de_passe}
                onChange={(e) =>
                  setChangePasswordData({
                    ...changePasswordData,
                    ancien_mot_de_passe: e.target.value,
                  })
                }
                placeholder="••••••••"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-foreground/40">
                Nouveau mot de passe
              </Label>
              <Input
                type="password"
                value={changePasswordData.nouveau_mot_de_passe}
                onChange={(e) =>
                  setChangePasswordData({
                    ...changePasswordData,
                    nouveau_mot_de_passe: e.target.value,
                  })
                }
                placeholder="••••••••"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-foreground/40">
                Confirmer le nouveau mot de passe
              </Label>
              <Input
                type="password"
                value={changePasswordData.confirm_mot_de_passe}
                onChange={(e) =>
                  setChangePasswordData({
                    ...changePasswordData,
                    confirm_mot_de_passe: e.target.value,
                  })
                }
                placeholder="••••••••"
              />
            </div>
          </div>
          <DialogFooter className="mt-8 flex gap-4">
            <Button
              variant="ghost"
              onClick={() => setShowChangePassword(false)}
            >
              Annuler
            </Button>
            <Button
              onClick={handleChangePassword}
              className="bg-accent text-white hover:bg-accent/90 font-bold"
            >
              Modifier le mot de passe
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Interest Edit Dialog */}
      <Dialog
        open={!!editInteretData}
        onOpenChange={() => setEditInteretData(null)}
      >
        <DialogContent className="sm:max-w-md p-10 rounded-lg">
          <DialogHeader className="mb-8">
            <DialogTitle className="text-3xl font-black text-primary">
              Modifier l'intérêt
            </DialogTitle>
          </DialogHeader>
          {editInteretData && (
            <div className="space-y-6">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-foreground/40">
                  Statut
                </Label>
                <Select
                  value={editInteretData.statut}
                  onValueChange={(value) =>
                    setEditInteretData({ ...editInteretData, statut: value })
                  }
                >
                  <SelectTrigger className="w-full h-12 rounded-lg font-bold">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUT_INTERET.map((s) => (
                      <SelectItem
                        key={s.value}
                        value={s.value}
                        className="font-bold"
                      >
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-foreground/40">
                  Message
                </Label>
                <Textarea
                  className="rounded-lg border-border/50 p-3"
                  value={editInteretData.message || ""}
                  onChange={(e) =>
                    setEditInteretData({
                      ...editInteretData,
                      message: e.target.value,
                    })
                  }
                  rows={4}
                />
              </div>
            </div>
          )}
          <DialogFooter className="mt-8 flex gap-4">
            <Button variant="ghost" onClick={() => setEditInteretData(null)}>
              Annuler
            </Button>
            <Button
              onClick={handleUpdateInterest}
              className="bg-accent text-white hover:bg-accent/90"
            >
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
